import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { DeleteModal } from "@View/components/DeleteModal";
import { deleteUser } from "@State/index";
import { connect } from "react-redux";
import DataTable from "@View/components/table/DataTable";
import { usePageRoot } from "@View/hooks/usePageRoot";
import { formatDate } from "@Localization/utils";
import { useHistory } from "react-router-dom";
import { Tooltip } from "@material-ui/core";
import DeleteIcon from "@View/components/svgComponents/DeleteIcon";
import EditIcon from "@View/components/svgComponents/EditIcon";
import DetailsIcon from "@View/components/svgComponents/DetailsIcon";
import UserDetailsPage from "@View/pages/AdminPO/user/UserDetailsPage";
import ButtonAddNew from "@View/components/buttons/ButtonAddNew";
import NewUserPage from "@View/pages/AdminPO/user/NewUserPage";
import EditUserPage from "@View/pages/AdminPO/user/EditUserPage";
import { useTooltipStyle } from "@View/style/components/tooltipStyles";
import { compareDatesForSorting } from "@Utils/dateActions";
import {
  canAddAbility,
  canAddNewUser,
  canDeleteCustomerUser,
  canEditCustomerUser,
  canEditEverything,
  canEditUser,
} from "@Security/stateHelpers";
import { ShouldHideUserEdit } from "@Constants/tokens";

//redux
const mapStateToProps = (state) => {
  return {
    users: state.customer?.customer?.users ?? [],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteUser: (userId, customerId) =>
      dispatch(deleteUser(userId, customerId)),
  };
};

const UsersTable = ({ users, deleteUser, customerId }) => {
  const { t } = useTranslation();
  const [deletedId, setDeletedId] = useState();
  const tooltipStyle = useTooltipStyle();

  const handleOpenModal = (event, userId) => {
    event.preventDefault();
    setDeletedId(userId);
  };

  const handleCloseModal = (event) => {
    event.preventDefault();
    setDeletedId();
  };

  const handleDelete = (event) => {
    event.preventDefault();
    deleteUser(deletedId, customerId);
    setDeletedId();
  };

  const [userId, setUserId] = React.useState();
  const [openDetails, setOpenDetails] = React.useState(false);

  const handleOpenDetails = () => {
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const [openNew, setOpenNew] = React.useState(false);

  const handleOpenNew = () => {
    setOpenNew(true);
  };

  const handleCloseNew = () => {
    setOpenNew(false);
  };

  const [openEdit, setOpenEdit] = React.useState(false);

  const handleOpenEdit = () => {
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const headTableUsers = [
    {
      field: "fullName",
      headerName: t("FullName"),
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "createdAt",
      headerName: t("inputs.Create Date"),
      flex: 0.7,
      headerAlign: "left",
      align: "left",
      valueGetter: (cell) => formatDate(cell.value),
      sortComparator: compareDatesForSorting,
    },
    {
      field: "status",
      headerName: t("inputs.Status"),
      flex: 1,
      headerAlign: "left",
      align: "left",
      valueGetter: (cell) => t(cell.row.status),
    },
    {
      field: "actions",
      headerName: " ",
      flex: 1,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderCell: (cell) => {
        return (
          <div className="tableIcons">
            <Tooltip title={t("buttons.Details")} classes={tooltipStyle}>
              <div
                onClick={() => {
                  setUserId(cell.row.id);
                  handleOpenDetails();
                }}
                className={tooltipStyle.element}
              >
                <DetailsIcon />
              </div>
            </Tooltip>

            {canEditEverything() && !ShouldHideUserEdit() && (
              <>
                {canEditCustomerUser(cell.row.id, cell.row.role) ? (
                  <Tooltip title={t("buttons.Edit")} classes={tooltipStyle}>
                    <div
                      onClick={() => {
                        setUserId(cell.row.id);
                        handleOpenEdit();
                      }}
                      className={tooltipStyle.element}
                    >
                      <EditIcon className="detailsIcon" />
                    </div>
                  </Tooltip>
                ) : (
                  <div
                    className={tooltipStyle.element}
                    style={{ cursor: "not-allowed", opacity: ".6" }}
                  >
                    <EditIcon className="detailsIcon" />
                  </div>
                )}
                {canEditUser() &&
                  (canDeleteCustomerUser(cell.row.role) ? (
                    <Tooltip title={t("buttons.Delete")} classes={tooltipStyle}>
                      <div
                        onClick={(event) => handleOpenModal(event, cell.row.id)}
                        className={tooltipStyle.element}
                      >
                        <DeleteIcon />
                      </div>
                    </Tooltip>
                  ) : (
                    // Keeps row icons aligned: rows the user can't delete get a dimmed icon instead of a gap
                    <div
                      className={tooltipStyle.element}
                      style={{ cursor: "not-allowed", opacity: ".6" }}
                    >
                      <DeleteIcon />
                    </div>
                  ))}
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <DataTable
        rows={users}
        columns={headTableUsers}
        toolbarButtons={
          canAddNewUser() &&
          canAddAbility() && <ButtonAddNew onClick={handleOpenNew} />
        }
      />
      {openNew ? (
        <NewUserPage
          customerId={customerId}
          prevPage={"customer"}
          open={openNew}
          handleOpen={handleOpenNew}
          handleClose={handleCloseNew}
        />
      ) : null}

      <UserDetailsPage
        userId={userId}
        open={openDetails}
        handleOpen={handleOpenDetails}
        handleClose={handleCloseDetails}
      />

      {openEdit ? (
        <EditUserPage
          userId={userId}
          open={openEdit}
          handleOpen={handleOpenEdit}
          handleClose={handleCloseEdit}
        />
      ) : null}

      <DeleteModal
        handleDelete={handleDelete}
        openModal={deletedId !== undefined}
        handleCloseModal={handleCloseModal}
      />
    </React.Fragment>
  );
};

UsersTable.propTypes = {
  users: PropTypes.array.isRequired,
  deleteUser: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersTable);
